// triggeredAnimations.js

const animationMap = {
	'fade-in-up': 'fadeIn',
	'fade-in': 'fadeInSimple',
	'fade-in-scale-up': 'fadeInScaleUp',
	'slide-in-left': 'slideInLeft',
	'slide-in-right': 'slideInRight',
	'scale-up': 'scaleUp',
	'remove-border-radius': 'removeBorderRadius',
	'add-border-radius': 'addBorderRadius',
	'scale-up-remove-radius': 'scaleUpRemoveRadius',
	'scale-down-add-radius': 'scaleDownAddRadius',
};

const keyframesFor = (animation, ctx) => {
	switch (animation) {
		case 'fade-in-up':
			return [
				{ opacity: 0, transform: 'translateY(30px)' },
				{ opacity: 1, transform: 'translateY(0)' },
			];

		case 'fade-in':
			return [{ opacity: 0 }, { opacity: 1 }];

		case 'fade-in-scale-up':
			return [
				{ opacity: 0, transform: 'scale(0.9)' },
				{ opacity: 1, transform: 'scale(1)' },
			];

		case 'slide-in-left':
			return [
				{ opacity: 0, transform: 'translateX(-100px)' },
				{ opacity: 1, transform: 'translateX(0)' },
			];

		case 'slide-in-right':
			return [
				{ opacity: 0, transform: 'translateX(100px)' },
				{ opacity: 1, transform: 'translateX(0)' },
			];

		case 'scale-up':
			return [{ transform: 'scale(0.6)' }, { transform: 'scale(1)' }];

		case 'remove-border-radius':
			return [{ borderRadius: ctx.initialRadius }, { borderRadius: '0px' }];

		case 'add-border-radius':
			return [{ borderRadius: '0px' }, { borderRadius: ctx.initialRadius || '12px' }];

		case 'scale-up-remove-radius':
			return [
				{
					transform: 'scale(0.6)',
					borderRadius: ctx.initialRadius,
				},
				{
					transform: 'scale(1)',
					borderRadius: '0px',
				},
			];

		case 'scale-down-add-radius':
			return [
				{
					transform: 'scale(1.05)',
					borderRadius: '0px',
				},
				{
					transform: 'scale(1)',
					borderRadius: ctx.initialRadius || '12px',
				},
			];

		default:
			return null;
	}
};

const playWithWAAPI = (target, animation, duration, delay) => {
	if (typeof target.animate !== 'function') {
		return false;
	}

	const computed = window.getComputedStyle(target);

	const initialRadius = computed.borderRadius || '0px';

	const frames = keyframesFor(animation, {
		initialRadius,
	});

	if (!frames) {
		return false;
	}

	target.animate(frames, {
		duration,
		delay,
		easing: 'ease',
		fill: 'both',
	});

	return true;
};

const resolveAnimationTarget = (el) => {
	const selector = 'hp-button, .hp-button, [data-hp-button]';

	if (el.matches(selector)) {
		return el;
	}

	const child = el.querySelector(selector);

	return child || el;
};

const getTriggers = (selector) => {
	if (!selector) {
		return [];
	}

	const trimmed = selector.trim();

	const results = [];

	if (trimmed.startsWith('#')) {
		const byId = document.getElementById(trimmed.slice(1));

		if (byId) {
			results.push(byId);
		}
	}

	const byId = document.getElementById(trimmed);

	if (byId) {
		results.push(byId);
	}

	try {
		document.querySelectorAll(trimmed).forEach((el) => {
			if (!results.includes(el)) {
				results.push(el);
			}
		});
	} catch {
		// Invalid selector.
	}

	return results;
};

const playOnce = (originalTarget, animation) => {
	const target = resolveAnimationTarget(originalTarget);

	if (target.dataset.hpTriggeredPlayed === '1') {
		return;
	}

	target.dataset.hpTriggeredPlayed = '1';

	const keyframe = animationMap[animation] || animation;

	const duration = parseInt(originalTarget.dataset.hpTriggerDuration || '600', 10);

	const delay = parseInt(originalTarget.dataset.hpTriggerDelay || '0', 10);

	const waapiRan = playWithWAAPI(target, animation, duration, delay);

	if (waapiRan) {
		target.dataset.hpTriggeredPlayed = '1';
		target.classList.add('hp-triggered-active');
		return;
	}

	target.style.removeProperty('animation-timeline');

	target.style.removeProperty('animation-range');

	target.style.setProperty('animation-timeline', 'auto');

	target.style.animation = 'none';

	void target.offsetHeight;

	target.style.animation = `${keyframe} ${duration}ms ease ${delay}ms 1 both`;

	target.classList.add('hp-triggered-active');
};

export const initTriggeredAnimations = () => {
	const targets = Array.from(document.querySelectorAll('[data-hp-triggered-animation]'));

	if (!targets.length) {
		return;
	}

	targets.forEach((target) => {
		const animation = target.dataset.hpTriggeredAnimation;

		if (!animation) {
			return;
		}

		const triggerSelector = target.dataset.hpTriggerSelector || '';

		const triggerEvent = target.dataset.hpTriggerEvent || 'click';

		const triggers =
			triggerSelector.trim() === '' ? [resolveAnimationTarget(target)] : getTriggers(triggerSelector);

		if (triggerEvent === 'viewport') {
			const observeElements = triggers.length ? triggers : [target];

			observeElements.forEach((el) => {
				const observer = new IntersectionObserver(
					(entries, obs) => {
						entries.forEach((entry) => {
							if (entry.isIntersecting) {
								playOnce(target, animation);

								target.classList.add(
									'hp-triggered-active'
								);
								obs.disconnect();
							}
						});
					},
					{
						threshold: 0.2,
					}
				);

				observer.observe(el);
			});

			return;
		}

		const eventName = triggerEvent === 'hover' ? 'mouseenter' : triggerEvent === 'focus' ? 'focus' : 'click';

		if (!triggers.length) {
			return;
		}

		triggers.forEach((trigger) => {
			trigger.addEventListener(
				eventName,
				() => {
					playOnce(target, animation);
				},
				{ once: true }
			);
		});
	});
};

export default initTriggeredAnimations;

// lib/blockAnimations.ts

export const getAnimationProps = (attributes = {}) => {
	const scroll = attributes.scrollAnimation || {};
	const triggered = attributes.triggeredAnimation || {};

	const animationClasses = [];

	if (scroll.animation) {
		animationClasses.push(scroll.animation);
		animationClasses.push(
			`anim-timing-${scroll.startOffset}-${scroll.endOffset}-${scroll.duration}-${scroll.delay}`
		);
	}

	if (triggered.animation) {
		animationClasses.push('hp-has-triggered-animation');
	}

	return {
		className: animationClasses,

		...(triggered.animation && {
			'data-hp-triggered-animation': triggered.animation,
			'data-hp-trigger-event': triggered.triggerEvent || 'viewport',
			'data-hp-trigger-selector': triggered.triggerSelector || '',
			'data-hp-trigger-duration': triggered.duration || 600,
			'data-hp-trigger-delay': triggered.delay || 0,
		}),
	};
};
